import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { KeyRound, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="forgot-password-page">
      <KeyRound className="h-6 w-6 text-[#FF3B30]" />
      <h1 className="mt-3 font-black tracking-tight text-3xl">Forgot your password?</h1>
      {sent ? (
        <div data-testid="forgot-sent" className="mt-5 rounded-xl border border-[#34C759]/40 bg-[#34C759]/[0.06] p-5 text-sm text-neutral-300">
          <MailCheck className="h-4 w-4 text-[#34C759] mb-2" />
          If an account exists for <span className="text-white font-semibold">{email}</span>, a reset
          link is on its way. Check your inbox (and spam) — the link works for 1 hour.
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 space-y-4">
          <p className="text-sm text-neutral-400">Enter your account email and we'll send a secure reset link.</p>
          <Input
            type="email"
            required
            data-testid="forgot-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="bg-[#121212] border-[#27272A]"
          />
          <Button type="submit" disabled={busy} data-testid="forgot-submit-btn" className="w-full h-11 rounded-full bg-white text-black hover:bg-neutral-200 font-semibold">
            {busy ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
      <Link to="/login" className="mt-6 inline-block text-xs text-[#007AFF] hover:underline">← Back to sign in</Link>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords don't match");
    if (pw.length < 8) return toast.error("Use at least 8 characters");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated — sign in with your new password");
      nav("/login");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="reset-password-page">
      <KeyRound className="h-6 w-6 text-[#FF3B30]" />
      <h1 className="mt-3 font-black tracking-tight text-3xl">Set a new password</h1>
      <form onSubmit={submit} className="mt-5 space-y-4">
        <PasswordInput
          data-testid="reset-password-input"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="New password (8+ characters)"
        />
        <PasswordInput
          data-testid="reset-password-confirm"
          value={pw2}
          onChange={(e) => setPw2(e.target.value)}
          placeholder="Repeat new password"
        />
        <Button type="submit" disabled={busy || !token} data-testid="reset-submit-btn" className="w-full h-11 rounded-full bg-white text-black hover:bg-neutral-200 font-semibold">
          {busy ? "Updating…" : "Update password"}
        </Button>
        {!token && <p className="text-xs text-[#FF3B30]">This link is missing its token — request a new reset email.</p>}
      </form>
    </div>
  );
}
